import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatSearchData, getSearchData, search } from 'services/myOrders';
import useRecaptcha from 'hooks/useRecaptcha';
import { MoTableData, MyOrder, SearchRequest } from 'ts/shared/interfaces/MOResponse';

import SkeletonMyOrders from './MyOrders';
import Switch from 'react-switch';
import { useLocation, useSearchParams } from 'react-router-dom';
import FormGroupDaterangePicker from 'components/ui/FormGroupDaterangePicker/FormGroupDaterangePicker';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupSelect from 'components/ui/FormGroupSelect/FormGroupSelect';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import MoTableReport from './MoTableReport';
import { MoContext } from 'contexts/MoContext';
import { endOfMonth, startOfMonth, subMonths } from 'date-fns';

import Dropdown from 'components/shared/Dropdown';
import { MainMoFilter, moSearchFilter } from 'constants/mo';
import { faCalendar, faCogs, faHand, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MoManualBook from './MoModal/MoManualBook';
import { MainContext } from 'contexts/MainContext';
import OrdersAssistantBridge from './Assistant/OrdersAssistantBridge';
import { hasPendingOrdersSearch, publishOrdersListFilter, useOrdersAction } from './Assistant/ordersActions';
import { highlightAssistantTargets } from 'utils/assistantHighlight';

interface MoSearch2Props {
  onChangeSearch: (data: MoTableData[], myOrders?: MyOrder[]) => void;
  tableData: MoTableData[];
  setSearchLoading: (val: boolean) => void;
  searchLoading: boolean;
  setMyOrders: (arg: MyOrder[]) => void;
}

const MoSearch2: FC<MoSearch2Props> = (props) => {
  const [t] = useTranslation();
  const { handleRecapcha, isRecaptchaReady } = useRecaptcha();
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const { user } = useContext(MainContext);
  const [urlParams] = useSearchParams();
  const orderIdParam = urlParams.get('orderId');
  const lastProcessedOrderId = useRef<string | null>(null);
  const initialSearchTriggered = useRef(false);
  const [id, setId] = useState('');
  const { pathname } = useLocation();
  const fetchRequest = useRef(new AbortController());
  const searchGeneration = useRef(0);
  const assistantWillSearch = useRef(false);
  if (hasPendingOrdersSearch()) {
    assistantWillSearch.current = true;
  }
  const {
    searchModel: model,
    setSearchModel: setModel,
    setMainFilter,
    mainFilter,
    setModelOptions,
    modelOptions,
  } = useContext(MoContext);
  const searchFilterArr = useRef(moSearchFilter);
  const modelRef = useRef(model);
  const [openManualBookModal, setOpenManualBookModal] = useState(false);
  useEffect(() => {
    modelRef.current = model;
  }, [model]);

  const switchStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    fontSize: 10,
    paddingRight: 2,
    border: '1px solid #999',
    borderRadius: '50%',
  };

  useEffect(() => {
    if (!orderIdParam) {
      lastProcessedOrderId.current = null;
      return;
    }
    if (!isRecaptchaReady || !model) return;
    if (lastProcessedOrderId.current === orderIdParam) return;

    const orderId = +orderIdParam;
    const newModel = {
      ...model,
      orderSegmentId: orderId,
      searchByOrderId: true,
      orderStatus: 'ALL',
    };
    setId(String(orderId));
    setModel(newModel);
    onClickSearch(newModel);

    sessionStorage.setItem('moInitialSearchDone', '1');
    lastProcessedOrderId.current = orderIdParam;
  }, [orderIdParam, isRecaptchaReady, model]); //eslint-disable-line

  useEffect(() => {
    if (!pathname?.toLowerCase().includes('my-orders')) {
      assistantWillSearch.current = false;
      return;
    }
    if (urlParams.get('orderId')) return;

    if (!isRecaptchaReady || !model || !modelOptions) {
      props.setSearchLoading(true);
      return;
    }

    if (assistantWillSearch.current || hasPendingOrdersSearch()) {
      assistantWillSearch.current = false;
      initialSearchTriggered.current = true;
      return;
    }

    if (initialSearchTriggered.current) return;
    initialSearchTriggered.current = true;
    onClickSearch();
  }, [pathname, isRecaptchaReady, model, modelOptions]); //eslint-disable-line

  useEffect(() => {
    if (!user) return;
    (async () => {
      if (user.branchId !== 5000) {
        const filterOut = [
          MainMoFilter.LastMonthSegments,
          MainMoFilter.CurrentMonthSegments,
          MainMoFilter.KeepDelRows,
        ];
        searchFilterArr.current = searchFilterArr.current.filter(
          (item) => !filterOut.includes(item.value as MainMoFilter)
        );
      }
      let newModel: any;
      const newResponse = await getSearchData();
      if (newResponse) {
        const {
          selectedBranchId,
          selectedDepartmentId,
          selectedUserCode,
          branchId,
          departmentId,
          userCode,
        } = newResponse.searchRequest;
        newModel = {
          ...(modelRef.current || newResponse.searchRequest),
          branchId: selectedBranchId || user?.branchId || branchId,
          departmentId: selectedDepartmentId || user?.departmentId || departmentId,
          userCode: selectedUserCode || user?.userCode || userCode,
          selectedBranchId: selectedBranchId,
          selectedDepartmentId: selectedDepartmentId,
          selectedUserCode: selectedUserCode,
        };
      }

      const options = formatSearchData(newModel);

      if (newModel && options) {
        const relatedBranches = options.branchesOptions.map((br) => {
          return {
            ...br,
            data: {
              ...br.data,
              visible:
                br.data?.departmentId === user.departmentId ||
                user.departmentId === -1 ||
                br.value === '-1',
            },
          };
        });

        const relatedUsers = options.usersOptions.map((u) => ({
          ...u,
          data: {
            ...u.data,
            visible: u.data?.branchId === user.branchId || user.branchId === -1 || u.value === '-1',
          },
        }));

        setModel(newModel);
        setModelOptions({
          ...options,
          branchesOptions: relatedBranches,
          usersOptions: relatedUsers,
        });
      }
    })();
  }, [user]); // eslint-disable-line

  const onClickSearch = async (newModel?: SearchRequest) => {
    const generation = ++searchGeneration.current;
    fetchRequest.current.abort();
    fetchRequest.current = new AbortController();
    props.setSearchLoading(true);
    props.onChangeSearch([], []);
    props.setMyOrders([]);
    const recaptchaToken = await handleRecapcha();
    if (generation !== searchGeneration.current) return false;
    if (!recaptchaToken) {
      props.setSearchLoading(false);
      return false;
    }
    const newTableData = await search(
      newModel || model!,
      recaptchaToken,
      fetchRequest.current.signal
    );

    if (generation !== searchGeneration.current) return false;

    if (newTableData) {
      props.onChangeSearch(newTableData.table, newTableData.data);
      props.setMyOrders(newTableData.data);

      if (window.innerWidth < 992 && newTableData?.data?.length) {
        const el = document.querySelector('.form-wrapper.m-search') as HTMLElement;
        if (el) {
          el.style.display = 'none';
        }
      }
    }
    props.setSearchLoading(false);
    return !!newTableData;
  };

  const onClickStop = () => {
    props.setSearchLoading(false);
    fetchRequest.current.abort();
    fetchRequest.current = new AbortController();
  };

  const onSelectDepartment = (departmentId: string) => {
    const relatedBranches =
      modelOptions?.branchesOptions.map((br) => {
        return {
          ...br,
          data: {
            ...br.data,
            visible:
              br.data?.departmentId === +departmentId ||
              br.data?.departmentId === -1 ||
              departmentId === '-1',
          },
        };
      }) || [];

    const relatedUsers =
      modelOptions?.usersOptions.map((u) => ({
        ...u,
        data: {
          ...u.data,
          visible:
            u.data?.departmentId === +departmentId ||
            u.data?.departmentId === -1 ||
            departmentId === '-1',
        },
      })) || [];

    setModel({
      ...model!,
      departmentId: +departmentId,
      branchId: -1,
      userCode: -1,
    });
    setModelOptions({
      ...modelOptions!,
      branchesOptions: relatedBranches,
      usersOptions: relatedUsers,
    });
  };

  const onSelectBranch = (branchId: string) => {
    const relatedUsers =
      modelOptions?.usersOptions.map((u) => ({
        ...u,
        data: {
          ...u.data,
          visible: u.data?.branchId === +branchId || u.data?.branchId === -1 || branchId === '-1',
        },
      })) || [];

    setModel({
      ...model!,
      branchId: +branchId,
      userCode: -1,
      usersOptions: relatedUsers,
    });
    setModelOptions({
      ...modelOptions!,
      usersOptions: relatedUsers,
    });
  };

  const onChangeId = (orderSegmentId: string) => {
    setId(orderSegmentId);
    const result = orderSegmentId
      .replace(/\D+/g, ' ')
      .trim()
      .split(' ')
      .filter((i) => i.length > 3)
      .map((i) => +i);
    if (result.length === 1) {
      setModel({ ...model!, orderSegmentId: result[0], orderSegmentIds: undefined });
    } else {
      if (!model?.orderSupplierPNR && !orderSegmentId) {
        setModel({ ...model!, orderSegmentIds: result, orderSegmentId: undefined, excludeDates: false });
      } else {
        setModel({ ...model!, orderSegmentIds: result, orderSegmentId: undefined });
      }
    }
  };

  const onChangeModel = (newModel: SearchRequest) => {
    setModel(newModel);

    if (props.tableData?.length) {
      const newTableData = props.tableData.map((item) => {
        const visibleOrder =
          newModel.orderProductType === item.productType || !newModel.orderProductType;
        const visibleSupplier =
          newModel.orderSupplier === item.supplierCode || newModel.orderSupplier === '';
        const visibleStatus =
          newModel.orderStatus.includes(item.status) || newModel.orderStatus === 'ALL';

        return {
          ...item,
          visible: visibleOrder && visibleSupplier && visibleStatus,
        };
      });
      props.onChangeSearch(newTableData);
    }
  };

  const onChnageSupplierPNR = (newModel: SearchRequest) => {
    if (!newModel.orderSupplierPNR && !id) {
      setModel({ ...newModel, excludeDates: false });
    } else {
      setModel(newModel);
    }
  };

  const onSelectFilter = async (filter: string) => {
    setMainFilter(filter);
    if (!model) return;

    switch (filter) {
      case MainMoFilter.LastMonthSegments:
        setModel({
          ...model,
          orderCheckIn: startOfMonth(subMonths(new Date(), 1)).toISOString(),
          orderCheckOut: endOfMonth(subMonths(new Date(), 1)).toISOString(),
          orderStatus: 'RQ;OK;HK;HX;VOUCHER',
          orderSupplier: '',
          orderProductType: '',
          departmentId: -1,
          branchId: -1,
          userCode: -1,
        });
        break;
      case MainMoFilter.CurrentMonthSegments:
        setModel({
          ...model,
          orderCheckIn: startOfMonth(new Date()).toISOString(),
          orderCheckOut: endOfMonth(new Date()).toISOString(),
          orderStatus: 'RQ;OK;HK;HX;VOUCHER',
          orderSupplier: '',
          orderProductType: '',
          departmentId: -1,
          branchId: -1,
          userCode: -1,
        });
        break;
      case MainMoFilter.KeepDelRows:
        const recaptchaToken = await handleRecapcha();
        if (!recaptchaToken) return;
        props.setMyOrders([]);
        props.onChangeSearch([], []);
        props.setSearchLoading(true);
        const newTableData = await search(
          { ...model!, orderFilter: 'KeepDelRows' },
          recaptchaToken,
          fetchRequest.current.signal
        );
        if (newTableData) {
          props.onChangeSearch(newTableData.table, newTableData.data);
          props.setMyOrders(newTableData.data);
          props.setSearchLoading(false);
        }
        break;
      default:
        break;
    }
  };

  const STAFF_PRESETS = [
    MainMoFilter.LastMonthSegments,
    MainMoFilter.CurrentMonthSegments,
    MainMoFilter.KeepDelRows,
  ];

  useOrdersAction(
    (action) => action.action === 'filter',
    (action) => {
      const preset = action.clearFilters ? MainMoFilter.NoFilter : action.preset;
      const allowed = preset === undefined || user?.branchId === 5000 || !STAFF_PRESETS.includes(preset as MainMoFilter);
      if (preset !== undefined && allowed && model) {
        if (preset === MainMoFilter.LastMonthSegments || preset === MainMoFilter.CurrentMonthSegments) {
          const month = preset === MainMoFilter.LastMonthSegments ? subMonths(new Date(), 1) : new Date();
          const next = {
            ...model,
            orderCheckIn: startOfMonth(month).toISOString(),
            orderCheckOut: endOfMonth(month).toISOString(),
            orderStatus: 'RQ;OK;HK;HX;VOUCHER',
            orderSupplier: '',
            orderProductType: '',
            departmentId: -1,
            branchId: -1,
            userCode: -1,
          };
          setModel(next);
          setMainFilter(preset);
          void onClickSearch(next);
        } else {
          void onSelectFilter(preset);
        }
      } else if (preset !== undefined && allowed) {
        setMainFilter(preset);
      }
      publishOrdersListFilter({
        text: action.clearFilters ? '' : action.tableFilter,
        columns: action.clearFilters ? [] : action.columnFilters || [],
        clearText: !!action.clearFilters || action.tableFilter !== undefined,
        clearColumns: !!action.clearFilters,
      });
      highlightAssistantTargets(preset !== undefined ? ['search', 'list'] : ['list']);
    },
  );

  const startWithFilterOption = (option: any, inputValue: string) => {
    const search = inputValue.toLowerCase();
    return (
      option.label?.toLowerCase().startsWith(search) ||
      option.value?.toLowerCase().startsWith(search)
    );
  };

  return (
    <Fragment>
      {!!model && !!modelOptions && (
        <div
          className="form-wrapper position-relative m-search"
          style={{ zIndex: 10 }}
          data-assistant-target="search"
        >
          <div className="m-search-item">
            <FormGroupDaterangePicker
              onChange={(dates) =>
                setModel({ ...model, orderCheckIn: dates.startDate, orderCheckOut: dates.endDate })
              }
              value={{ startDate: model.orderCheckIn, endDate: model.orderCheckOut }}
              format="dd/MM/yy"
              movablePlaceholder={'Dates'}
              movablePlaceholderIcon={faCalendar}
              showSeparateCalendars={true}
              locale='en'
            />
          </div>
          <div className="m-search-item m-search-item-md">
            <FormGroupSelect
              className=""
              onChange={(status) => onChangeModel({ ...model, orderStatus: status })}
              value={model.orderStatus}
              options={modelOptions?.statusesOptions}
              placeholder={t('mo.status_full')}
              showHtmlPrev={true}
              movablePlaceholder={t('mo.status_full')}
            />
          </div>
          <div className="m-search-item flex-row">
            <FormGroupSelect
              className="m-search-item-sm mr-5"
              onChange={(orderSupplier) => onChangeModel({ ...model, orderSupplier })}
              value={model.orderSupplier}
              options={modelOptions?.supplierOptions}
              showHtmlPrev={true}
              movablePlaceholder={'Supplier'}
              movablePlaceholderAlwaysSmall={true}
              disabled={!user?.branch?.hotelDisplaySupplierName}
              filterOption={startWithFilterOption}
            />
            <FormGroupSelect
              className="m-search-item-sm"
              onChange={(orderProductType) => onChangeModel({ ...model, orderProductType })}
              value={model.orderProductType}
              options={modelOptions?.typesOptions}
              placeholder={'Type'}
              showHtmlPrev={true}
              movablePlaceholder={'Type'}
              movablePlaceholderAlwaysSmall={true}
              filterOption={startWithFilterOption}
            />
          </div>
          <div className="m-search-item">
            <FormGroupSelect
              className="select-custom__menu-long"
              onChange={onSelectDepartment}
              value={model.departmentId + ''}
              options={modelOptions?.departmentsOptions}
              disabled={!!model.selectedDepartmentId}
              showHtmlPrev={true}
              movablePlaceholder={'Department'}
              filterOption={startWithFilterOption}
            />
          </div>
          <div className="m-search-item">
            <FormGroupSelect
              className=" select-custom__menu-long"
              onChange={onSelectBranch}
              value={model.branchId + ''}
              options={modelOptions?.branchesOptions.filter((br) => br.data?.visible)}
              disabled={!!model.selectedBranchId}
              showHtmlPrev={true}
              movablePlaceholder={'Branch'}
              filterOption={startWithFilterOption}
            />
          </div>
          <div className="m-search-item m-search-item-one flex-row" style={{ marginInlineEnd: 0 }}>
            <div className="flex-one">
              <FormGroupSelect
                className="select-custom__menu-longer select-custom__menu-left"
                onChange={(userCode) => setModel({ ...model, userCode: +userCode })}
                value={model.userCode + ''}
                options={modelOptions?.usersOptions.filter((u) => u.data?.visible)}
                disabled={!!model.selectedUserCode}
                showHtmlPrev={true}
                movablePlaceholder={'User'}
                filterOption={startWithFilterOption}
              />
            </div>

            {!props.searchLoading && (
              <Button
                title="GO"
                onClick={() => onClickSearch()}
                className="btn-go btn btn-primary"
              />
            )}
            {props.searchLoading && (
              <Button
                title={''}
                icon={faTimes}
                onClick={() => onClickStop()}
                className="btn btn-danger"
              />
            )}
          </div>

          <div className="m-search-row-filter">
            <div className="m-search-item mb-5">
              <div className="flex-row">
                <div className="flex-center mr-5">
                  <Switch
                    checked={model.searchByOrderId}
                    onChange={(searchByOrderId) => setModel({ ...model, searchByOrderId })}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    offColor="#ccc"
                    onColor={colorContrast}
                    height={16}
                    width={32}
                    handleDiameter={24}
                    checkedHandleIcon={<div style={switchStyle}>Ord.</div>}
                    uncheckedHandleIcon={<div style={switchStyle}>Seg.</div>}
                  />
                </div>

                <FormGroup
                  className="flex-one"
                  onChange={onChangeId}
                  value={id}
                  label=""
                  movablePlaceholder={t(model.searchByOrderId ? 'mo.order_id' : 'mo.segment_id')}
                  onKeyDown={(e) => e.key === 'Enter' && onClickSearch()}
                />
              </div>
            </div>
            <div className="m-search-item m-search-item-md form-group-yellow">
              <FormGroup
                onChange={(val) => onChnageSupplierPNR({ ...model, orderSupplierPNR: val })}
                value={model.orderSupplierPNR || ''}
                label=""
                movablePlaceholder={t('mo.supplier_pnr')}
                className=""
              />
            </div>
            <div className="m-search-item form-group-yellow flex-row">
              <div className='m-search-item-sm text-xsm m-search-item-checkbox'>
                <Checkbox
                  onChange={(val) => setModel({ ...model, excludeDates: val })}
                  checked={model.excludeDates}
                  label={t('Exclude Dates')}
                  disabled={!model.orderSupplierPNR && !model.searchByOrderId && !id}
                />

              </div>
              <div className="m-search-item-sm ml-5">
                <FormGroup
                  onChange={(val) => setModel({ ...model, orderBackOfficeId: val })}
                  value={model.orderBackOfficeId || ''}
                  label=""
                  movablePlaceholder={t('mo.backoffice_id')}
                />
              </div>
            </div>
            <div className="m-search-item form-group-yellow">
              <FormGroup
                onChange={(val) => setModel({ ...model, orderPaxName: val })}
                value={model.orderPaxName || ''}
                label=""
                movablePlaceholder={t('mo.passenger_name')}
              />
            </div>
            <div className="m-search-item form-group-yellow ">
              <FormGroup
                onChange={(val) => setModel({ ...model, orderItem: val })}
                value={model.orderItem || ''}
                label=""
                movablePlaceholder={t('mo.item')}
              />
            </div>
            <div className="m-search-item flex-row m-search-item-one" style={{ marginInlineEnd: 0 }}>
              <div className="flex-one mr-5" style={{ width: 'calc(100% - 200px)' }}>
                <FormGroupSelect
                  className="m-search-filter"
                  onChange={onSelectFilter}
                  value={mainFilter}
                  options={searchFilterArr.current}
                  movablePlaceholder={'Filter'}
                  movablePlaceholderAlwaysSmall={true}
                />
              </div>

              <div className="flex-row">
                <div className="flex-v-center" style={{ width: 100 }}>
                  <Switch
                    checked={model.searchByCreateDate}
                    onChange={(val) => setModel({ ...model, searchByCreateDate: val })}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    offColor="#ccc"
                    onColor={colorContrast}
                    height={16}
                    width={32}
                    handleDiameter={22}
                    checkedHandleIcon={<div style={switchStyle}></div>}
                    uncheckedHandleIcon={<div style={switchStyle}></div>}
                    id="switchCreate"
                  />
                  <label htmlFor="switchCreate" className="ml-5 cur-poi text-xsm">
                    {t(model.searchByCreateDate ? 'mo.creation_date' : 'mo.check_in')}
                  </label>
                </div>

                <Dropdown
                  openerEmpty={true}
                  byClick={true}
                  left={-120}
                  opener={
                    <button className="btn btn-default" title="">
                      <FontAwesomeIcon icon={faCogs} />
                    </button>
                  }
                >
                  <div className="c-dropdown-row">
                    <div className="flex-row" onClick={() => setOpenManualBookModal(true)}>
                      <FontAwesomeIcon icon={faHand} className="mr-5" />
                      <span>Manual Booking</span>
                    </div>
                  </div>
                </Dropdown>

                <div className="ml-10">
                  <MoTableReport searchParams={model} table={props.tableData} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {openManualBookModal && (
        <MoManualBook
          openModal={openManualBookModal}
          setOpenModal={setOpenManualBookModal}
        />
      )}
      <OrdersAssistantBridge
        ready={
          !!model &&
          !!modelOptions &&
          isRecaptchaReady &&
          pathname.toLowerCase().includes('my-orders')
        }
        onSearch={async (request, view) => {
          if (view.segmentText !== undefined) setId(view.segmentText ?? '');
          return onClickSearch(request);
        }}
      />
      {!model && <SkeletonMyOrders step={1} />}
    </Fragment>
  );
};

export default MoSearch2;
